/**
 * Helpers for the Live Subscription Status page: tabs, demand filters, number
 * formatting, summary stats and CSV export.
 */
import { formatFilingDate } from './upcomingIpos';

export const isLive = (issue) => issue.status === 'open' || issue.status === 'closing_today';

export const SUBSCRIPTION_TABS = [
  { key: 'mainboard', label: 'Mainboard IPOs', test: (issue) => issue.board === 'mainboard' },
  { key: 'sme', label: 'SME IPOs', test: (issue) => issue.board === 'sme' },
  { key: 'closed', label: 'Closed Issues', test: (issue) => issue.status === 'closed' || issue.status === 'listed' },
];

export const subscriptionTabByKey = (key, tabs = SUBSCRIPTION_TABS) => tabs.find((tab) => tab.key === key) || tabs[0];

// SME page (/sme-ipo-subscription): every row is an SME issue
export const SME_SUBSCRIPTION_TABS = [
  { key: 'live', label: 'Live & Upcoming SME IPOs', test: (issue) => issue.board === 'sme' && !['closed', 'listed'].includes(issue.status) },
  { key: 'closed', label: 'Closed SME Issues', test: (issue) => issue.board === 'sme' && ['closed', 'listed'].includes(issue.status) },
];

export const DEMAND_FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'over', label: '>1x (Oversubscribed)', test: (issue) => issue.total > 1 },
  { key: 'heavy', label: '>10x (Heavy)', test: (issue) => issue.total > 10 },
  { key: 'retail', label: 'Retail Fav (>5x)', test: (issue) => (issue.retail ?? 0) > 5 },
];

export const filterSubscriptions = (issues, { tab = 'mainboard', demand = 'all', query = '', tabs = SUBSCRIPTION_TABS } = {}) => {
  const needle = query.trim().toLowerCase();
  const demandTest = (DEMAND_FILTERS.find((filter) => filter.key === demand) || DEMAND_FILTERS[0]).test;
  return issues
    .filter(subscriptionTabByKey(tab, tabs).test)
    .filter(demandTest)
    .filter(
      (issue) =>
        !needle || [issue.company, issue.symbol, issue.sector].filter(Boolean).join(' ').toLowerCase().includes(needle)
    );
};

export const formatMultiple = (value) => (value === null || value === undefined ? '-' : value.toFixed(2));

export const formatCrores = (value) =>
  value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// 4481885 -> '44.8L'
export const formatLakhs = (count) => `${(count / 100000).toFixed(1)}L`;

// 505811384 -> '50.58 Cr', 4481885 -> '44.8L', 9880 -> '9,880'
export const formatShareCount = (count) => {
  if (!count) return '-';
  if (count >= 1e7) return `${(count / 1e7).toFixed(2)} Cr`;
  if (count >= 1e5) return formatLakhs(count);
  return Math.round(count).toLocaleString('en-IN');
};

// '16:58' -> '4:58 PM'
export const formatClock = (hhmm) => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
};

// '2026-09-09T16:58' -> { date: '09-Sep-2026', time: '16:58' }
export const splitAsOn = (asOn) => {
  const [date, time] = asOn.split('T');
  return { date: formatFilingDate(date), time };
};

const IST_FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

// API timestamps are UTC ('2026-09-21T14:02:37+00:00'); the page shows IST -> '2026-09-21T19:32'
export const toIstStamp = (iso) => {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return null;
  const parts = Object.fromEntries(IST_FORMAT.formatToParts(date).map(({ type, value }) => [type, value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

/**
 * Adapts a row from utils/subscriptionMapper.js (GET /api/market/subscription)
 * to this page's table shape. The feed has no issue size or application count:
 * the offer amount is the shares offered for bidding (NSE's "Total" row, which
 * excludes the anchor portion) at the upper price band, and demand is shown as
 * shares bid.
 */
export const toSubscriptionStatusRow = (row, fallbackRefreshedAt = null) => ({
  ...row,
  status: row.phase,
  detail: [row.symbol, row.priceBand !== '—' ? row.priceBand : null].filter(Boolean).join(' · '),
  issueCr:
    row.sharesOffered && row.priceHigh ? Math.round(((row.sharesOffered * row.priceHigh) / 1e7) * 100) / 100 : null,
  asOn: toIstStamp(row.refreshedAt || fallbackRefreshedAt),
});

// An SME issue from the NSE schedule (utils/smeIpos.js row) that hasn't
// opened yet, so it has no bidding figures -- shown with an "Opens" marker
export const upcomingSmeToStatusRow = (row) => ({
  id: row.symbol || row.id,
  symbol: row.symbol,
  company: row.company,
  board: 'sme',
  status: row.status,
  phase: row.status,
  openOn: row.openOn,
  closeOn: row.closeOn,
  priceBand: row.priceBand,
  detail: [row.symbol, row.priceBand].filter(Boolean).join(' · '),
  issueCr: null,
  qib: null,
  snii: null,
  bnii: null,
  nii: null,
  retail: null,
  emp: null,
  total: null,
  sharesBid: null,
  sharesOffered: null,
  asOn: null,
});

// Badge style for the total multiple
export const totalTone = (total) => {
  if (total >= 100) return 'blockbuster';
  return total >= 1 ? 'over' : 'under';
};

export const shortName = (company) => company.replace(/\s+(Ltd\.?|Limited)$/i, '');

export const latestSync = (issues) => issues.map((issue) => issue.asOn).filter(Boolean).sort().pop() || null;

export const computeSubscriptionStats = (issues) => {
  const live = issues.filter(isLive);
  const withTotals = issues.filter((issue) => typeof issue.total === 'number');
  const liveWithTotals = withTotals.filter(isLive);
  const pool = liveWithTotals.length ? liveWithTotals : withTotals;
  const top = pool.reduce((best, issue) => (!best || issue.total > best.total ? issue : best), null);
  const retail = issues.map((issue) => issue.retail).filter((value) => typeof value === 'number');
  return {
    total: issues.length,
    liveCount: live.length,
    top,
    topIsLive: liveWithTotals.length > 0,
    avgRetail: retail.length ? retail.reduce((sum, value) => sum + value, 0) / retail.length : null,
    closingToday: issues.filter((issue) => issue.status === 'closing_today'),
  };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const STATUS_LABELS = {
  closing_today: 'Closing today',
  open: 'Open',
  upcoming: 'Upcoming',
  closed: 'Closed',
  listed: 'Listed',
};

export const buildSubscriptionCsv = (issues) => {
  const header = [
    'Company', 'Symbol', 'Board', 'Status', 'Price Band', 'Closing Date', 'Offer Amount (INR Cr)', 'QIB (x)', 'sNII (x)',
    'bNII (x)', 'NII (x)', 'Retail (x)', 'Employee (x)', 'Total (x)', 'Shares Bid', 'Shares Offered', 'Subscription As On (IST)',
  ];
  const lines = issues.map((issue) =>
    [
      issue.company,
      issue.symbol,
      issue.board === 'sme' ? 'SME' : 'Mainboard',
      STATUS_LABELS[issue.status],
      issue.priceBand,
      issue.closeOn,
      issue.issueCr,
      issue.qib,
      issue.snii,
      issue.bnii,
      issue.nii,
      issue.retail,
      issue.emp,
      issue.total,
      issue.sharesBid,
      issue.sharesOffered,
      issue.asOn?.replace('T', ' '),
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
